import { CalendarDays } from "@tamagui/lucide-icons";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import React from "react";
import { XStack, YStack } from "tamagui";
import { formatDate, getDayJsFromDate } from "../../../core/dayJs";
import { usePlanNavigation } from "../../../plans/usePlanNavigation";
import { Label } from "../../../ui/Label";
import { Subtext } from "../../../ui/Subtext";
import { type Event, isEvent } from "../../scheduleTypes";
import type { BusinessPlanModel } from "../BusinessPlanModel";
import { useScheduleItems } from "../tabs/useScheduleItems";

export const BusinessScheduleNextUp: React.FC<{
  businessPlan: BusinessPlanModel;
}> = observer(({ businessPlan }) => {
  const { groupedList } = useScheduleItems(businessPlan.uid, false);

  const { navigate } = usePlanNavigation();

  const eventItems: Event[] = React.useMemo(() => {
    const items: Event[] = [];

    groupedList.forEach((item) => {
      if (isEvent(item)) {
        items.push(item);
      }
    });

    return items;
  }, [groupedList]);

  const nextUpScheduleItem = eventItems[0];

  if (!nextUpScheduleItem) {
    return null;
  }

  const date = new Date(nextUpScheduleItem.date);

  // Only show if less than 30 days
  const eventDate = dayjs(nextUpScheduleItem.date);
  const today = dayjs();

  const diffInDays = eventDate.diff(today, "day");

  if (diffInDays >= 30) {
    return null;
  }

  return (
    <XStack
      gap="$space.medium"
      alignItems="center"
      justifyContent="space-between"
      paddingHorizontal={"$large"}
      paddingVertical={"$medium"}
      cursor="pointer"
      onPress={() =>
        navigate("BusinessScheduleItem", {
          businessUid: businessPlan.uid,
          itemUid: nextUpScheduleItem.uid,
        })
      }
    >
      <CalendarDays />

      <YStack gap="$small" flex={1}>
        <Label color={"$color"}>{nextUpScheduleItem.title}</Label>
        <Subtext color={"$color"}>
          {getDayJsFromDate(date)} - {formatDate(date)}
        </Subtext>
      </YStack>
    </XStack>
  );
});
